import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Pencil, Trash2, LogOut, Copy, Tv, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';

export default function AdminDashboard() {
  const nav = useNavigate();
  const { user, loading, tournaments, logout, deleteTournament, duplicateTournament } = useApp();
  const { toast } = useToast();

  React.useEffect(() => { if (!loading && !user) nav('/admin/login'); }, [user, loading, nav]);

  if (!user) return null;

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast({ title: 'Код скопирован', description: `${code} в буфере обмена` });
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/magnum-logo.png" alt="MAGNUM" className="h-11 w-11 rounded-md object-contain" />
            <div>
              <div className="text-base font-extrabold leading-tight sm:text-lg">MAGNUM <span className="text-red-600">ведущий дом спортивного покера</span></div>
              <div className="-mt-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500">Админ-панель</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden text-right text-sm sm:block">
              <div className="font-semibold text-neutral-900">{user.name}</div>
              <div className="text-xs text-neutral-500">{user.email}</div>
            </div>
            {user.isSuperAdmin && (
              <Button variant="outline" onClick={() => nav('/admin/accounts')}>
                <Users className="mr-2 h-4 w-4" /> Аккаунты
              </Button>
            )}
            <Button variant="outline" onClick={() => { logout(); nav('/'); }}><LogOut className="mr-2 h-4 w-4" /> Выйти</Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">Турниры</h1>
            <p className="mt-1 text-sm text-neutral-600">Создавайте часы, делитесь 2-значными кодами, проводите турниры.</p>
          </div>
          <Button onClick={() => nav('/admin/tournament/new')} className="h-11 bg-red-600 text-white hover:bg-red-700">
            <Plus className="mr-2 h-4 w-4" /> Новый турнир
          </Button>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tournaments.length === 0 && (
            <Card className="col-span-full rounded-2xl border-dashed border-neutral-300 bg-white">
              <CardContent className="flex flex-col items-center py-14 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600"><Clock className="h-6 w-6" /></div>
                <div className="mt-4 text-lg font-semibold">Пока нет турниров</div>
                <p className="mt-1 text-sm text-neutral-500">Создайте первый турнир, чтобы получить 2-значный код.</p>
                <Button onClick={() => nav('/admin/tournament/new')} className="mt-6 bg-red-600 text-white hover:bg-red-700"><Plus className="mr-2 h-4 w-4" /> Новый турнир</Button>
              </CardContent>
            </Card>
          )}
          {tournaments.map((t) => {
            const level = t.blinds[t.currentLevelIndex];
            const statusText = t.isRunning ? (t.isPaused ? 'Пауза' : 'В эфире') : 'Ожидание';
            return (
              <Card key={t.id} className="group rounded-2xl border-neutral-200 transition-all hover:-translate-y-0.5 hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-lg font-bold text-neutral-900">{t.name}</div>
                      {t.subtitle && <div className="mt-0.5 text-sm text-red-600">{t.subtitle}</div>}
                    </div>
                    <Badge variant={t.isRunning && !t.isPaused ? 'default' : 'secondary'} className={t.isRunning && !t.isPaused ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-neutral-100 text-neutral-600'}>
                      {statusText}
                    </Badge>
                  </div>

                  <div className="mt-5 flex items-center gap-3 rounded-lg bg-neutral-900 px-4 py-3">
                    <Tv className="h-5 w-5 text-neutral-400" />
                    <div className="font-clock text-2xl font-bold tracking-wider text-white">{t.code}</div>
                    <button onClick={() => copy(t.code)} className="ml-auto text-neutral-400 hover:text-white" title="Копировать">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-md bg-neutral-100 py-2">
                      <div className="text-neutral-500">Игроки</div>
                      <div className="font-semibold text-neutral-900">{t.players.remaining}/{t.players.total}</div>
                    </div>
                    <div className="rounded-md bg-neutral-100 py-2">
                      <div className="text-neutral-500">Уровень</div>
                      <div className="font-semibold text-neutral-900">{level?.isBreak ? 'Перерыв' : level?.level || 1}</div>
                    </div>
                    <div className="rounded-md bg-neutral-100 py-2">
                      <div className="text-neutral-500">Блайнды</div>
                      <div className="font-semibold text-neutral-900">
                        {level?.isBreak ? '—' : `${level?.sb}/${level?.bb}`}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => nav(`/admin/tournament/${t.id}`)} className="bg-red-600 text-white hover:bg-red-700">
                      <Play className="mr-1 h-4 w-4" /> Управлять
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => nav(`/admin/tournament/${t.id}/edit`)}>
                      <Pencil className="mr-1 h-4 w-4" /> Редактировать
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(`/clock/${t.code}`, '_blank')}>
                      <Tv className="mr-1 h-4 w-4" /> Открыть ТВ
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                     
                      onClick={async () => {
                        try {
                          const copy = await duplicateTournament(t.id);
                          toast({ title: 'Скопировано', description: `${copy.name} — код ${copy.code}` });
                        } catch (e) {
                          toast({ title: 'Ошибка', description: e.message, variant: 'destructive' });
                        }
                      }}
                    >
                      <Copy className="mr-1 h-4 w-4" /> Копировать
                    </Button>
                    <Button size="sm" variant="ghost" className="text-neutral-500 hover:bg-red-50 hover:text-red-600" onClick={() => {
                      if (window.confirm(`Удалить «${t.name}»?`)) { deleteTournament(t.id); toast({ title: 'Удалено', description: t.name }); }
                    }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
